<template>
  <div
    v-if="!subscriptionIsFreeTrial && $store.state.idea.storyMode.common.completedSteps.directCostsCompleted === true && isDirectCostsVisible"
    class="journal-content dynamic-height"
    :class="{'dynamic-height': !isOption(directCostData, 'breakdownSection') && !isOption(directCostData, 'timelineSection') && !isOption(directCostData, 'tableSection')}"
  >
    <div class="content-menus">
      <div v-if="moreVisible === true" class="view-type">
        <template v-if="directCost.length !== 0">
          <a
            class="orange"
            :class="{'orange-off': !isOption(directCostData, 'breakdownSection')}"
            @click="enableBreakdown"
          >
            {{ $t('pages.businessPlan.sections.breakdown') }}
          </a>
          <a
            class="orange"
            :class="{'orange-off': !isOption(directCostData, 'timelineSection')}"
            @click="enableTimeline"
          >
            {{ $t('pages.businessPlan.sections.timeline') }}
          </a>
          <a
            class="orange"
            :class="{'orange-off': !isOption(directCostData, 'tableSection')}"
            @click="enableTable"
          >
            {{ $t('pages.businessPlan.sections.table') }}
          </a>
        </template>
        <p v-if="directCost.length === 0">
          {{ $t('pages.businessPlan.sections.youDontHaveAnyDataOnThisPage') }}
        </p>
      </div>
      <div v-if="canEdit" class="mini-actions">
        <toggle-page-items-button
          :visible="moreVisible"
          @click="toggleMore"
        />
        <toggle-view-button
          :disabled="!cardExist"
          :horizontal="!isOption(directCostData, 'gridListView')"
          @click="toggleRotate"
        />
        <toggle-notes-button
          :disabled="!moreDetails"
          :visible="isOption(directCostData, 'moreDetailsDisplay')"
          @click="enableMoreDetails"
        />
        <edit-section-link route-name="business-guide-projections-direct-costs" />
      </div>
    </div>

    <div class="sub chapter">
      <header>
        <!-- Title section -->
        <editable-title
          v-model="directCostData.title"
          tag="h1"
          :disabled="!canEdit || shareMode"
          @save="onSave"
        />
        <!-- Title section -->
        <hr>
        <!-- Description section -->
        <editable-description
          v-model="directCostData.subTitle"
          :empty-text="$t('addMoreDetails.addNoteText')"
          :disabled="!canEdit || shareMode"
          @save="onSave"
        />
        <!-- Description section -->
      </header>

      <template v-if="directCost.length !== 0">
        <!-- Breakdown chart -->
        <div v-if="isOption(directCostData, 'breakdownSection')" class="switcher-inner-wrapper breakdown-chart">
          <direct-costs-breakdown
            :chart-adjustment="chartAdjustment"
            :update-chart="updateCharts"
          />
        </div>
        <!-- /Breakdown chart -->

        <!-- Timeline chart -->
        <div v-if="isOption(directCostData, 'timelineSection')" class="switcher-inner-wrapper timeline-chart">
          <direct-costs-timeline
            :switch-selector="switchSelector"
            :chart-adjustment="chartAdjustment"
            :update-chart="updateCharts"
          />
        </div>
        <!-- /Timeline chart -->

        <!-- Finance Table -->
        <div v-if="isOption(directCostData, 'tableSection')" class="switcher-inner-wrapper table-chart">
          <direct-costs-table
            :table-adjustment="tableAdjustment"
            :update-chart="updateCharts"
          />
        </div>
        <!-- /Finance Table -->
      </template>

      <!-- More Details section -->
      <template v-if="moreDetails && isOption(directCostData, 'moreDetailsDisplay')">
        <div class="details">
          <editable-title
            v-model="directCostData.moreDetailsTitle"
            class="bold"
            tag="p"
            :disabled="!canEdit"
            @save="onSave"
          />
          <p v-html="moreDetails" />
        </div>
      </template>
      <!-- More Details section -->
    </div>
  </div>
</template>

<script>
import DirectCostsBreakdown from '@/views/Home/StoryMode/Forecast/DirectCosts/Charts/DirectCostsBreakdown'
import DirectCostsTable from '@/views/Home/StoryMode/Forecast/DirectCosts/Tables/DirectCostsTable'
import DirectCostsTimeline from '@/views/Home/StoryMode/Forecast/DirectCosts/Charts/DirectCostsTimeline'
import EditSectionLink from '@/views/Home/StoryMode/Components/BusinessPlan/EditSectionLink'
import EditableDescription from '@/components/_v2/EditableDescription'
import EditableTitle from '@/components/_v2/EditableTitle'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import MixinUserSubscription from '@/mixins/permissions/userSubscription'
import ToggleNotesButton from '@/views/Home/StoryMode/Components/BusinessPlan/ToggleNotesButton'
import TogglePageItemsButton from '@/views/Home/StoryMode/Components/BusinessPlan/TogglePageItemsButton'
import ToggleViewButton from '@/views/Home/StoryMode/Components/BusinessPlan/ToggleViewButton'
import journalMethods from '@/mixins/journalMethods'

export default {
  name: 'DirectCosts',

  components: {
    ToggleNotesButton,
    TogglePageItemsButton,
    ToggleViewButton,
    EditSectionLink,
    DirectCostsBreakdown,
    DirectCostsTimeline,
    DirectCostsTable,
    EditableTitle,
    EditableDescription
  },

  mixins: [
    journalMethods,
    MixinIdeaRoles,
    MixinUserSubscription
  ],

  props: {
    allPagesTextData: {
      type: Object,
      default: () => ({})
    },
    shareMode: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      cardExist: false,
      directCostData: {},

      moreVisible: false,
      updateCharts: true,

      moreDetails: this.$store.state.idea.storyMode.common.moreDetails.directCostsMoreDetails,
      directCost: this.$store.state.idea.storyMode.forecast.directCosts.directCosts,

      chartAdjustment: {
        chartHeight: 260,
        headingTitleFontSize: '18px',
        xLabelTitleFontSize: '10px',
        yLabelTitleFontSize: '12px',
        yaxisLabelsMinWidth: {
          breakDown: 0,
          timeLine: 0
        }
      },
      tableAdjustment: {
        journalPage: true,
        period: 'yearly',
        tableFirstColumnWidth: 250,
        columnWidth: 118
      },
      switchSelector: 'direct-costs'
    }
  },

  computed: {
    isDirectCostsVisible () {
      const index = this.$store.state.idea.journal.journalSwitchBars.indexOf(
        this.$store.state.idea.journal.journalSwitchBars.find(journalElement => journalElement.sectionName === 'DirectCosts')
      )

      return !this.$store.state.idea.journal.journalSwitchBars[index].options.includes('switch')
    },

    isTitleEnabled () {
      return this.directCostData.title
    },

    isMoreDetailsTitleEnabled () {
      return this.directCostData.moreDetailsTitle
    }
  },

  watch: {
    allPagesTextData () {
      this.directCostData = Object.assign({}, this.allPagesTextData)
    }
  },

  mounted () {
    this.directCostData = Object.assign({}, this.allPagesTextData)
  },

  methods: {
    toggleRotate () {
      if (this.cardExist) {
        this.toggleOption(this.directCostData, 'gridListView')
        this.directCostData = Object.assign({}, this.allPagesTextData)
        this.onSave()
      }
    },

    enableMoreDetails () {
      if (this.moreDetails) {
        this.toggleOption(this.directCostData, 'moreDetailsDisplay')
        this.directCostData = Object.assign({}, this.allPagesTextData)
        this.onSave()
      }
    },

    onSave () {
      this.$emit('save', this.directCostData)
    },

    enableTimeline () {
      this.toggleOption(this.directCostData, 'timelineSection')
      this.directCostData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    },

    enableBreakdown () {
      this.toggleOption(this.directCostData, 'breakdownSection')
      this.directCostData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    },

    enableTable () {
      this.toggleOption(this.directCostData, 'tableSection')
      this.directCostData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    }
  }
}
</script>
